import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Ban, ChevronLeft, ShieldCheck } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { DangerDialog } from '@/components/DangerDialog'
import { useToast } from '@/providers/UiProviders'
import { createFourEyesRequest } from '@/lib/adapters'
import { fetchCountriesOverlay, ovText, GEO_STR as S, type CountryOverlayRow } from './geoApi'

/**
 * Umumiy davlat-cheklov ekrani (A11.05/06/07/08). REAL `/admin/countries`
 * ro'yxati + per-davlat greenfield overlay (`overlayPath`/`overlayKey`, fail-soft
 * bo'sh). Har davlatni **bloklash/ochish** — CountriesScreen kabi FOUR-EYES
 * producer (`createFourEyesRequest` → boshqa admin tasdiqlaydi). Bitta cheklov
 * o'lchovi (funksiya/to'lov/kontent/stadiya) `overlayField` orqali ko'rsatiladi.
 */
export function RestrictionScreen({
  screenId,
  title,
  note,
  icon,
  overlayPath,
  overlayKey,
  overlayField,
  overlayHeader,
  renderOverlay,
}: {
  screenId: string
  title: string
  note: string
  icon: ReactNode
  overlayPath: string
  overlayKey: string
  overlayField: string
  overlayHeader: string
  /** Overlay ustuni maxsus ko'rinishi (berilmasa matn). */
  renderOverlay?: (row: CountryOverlayRow) => ReactNode
}) {
  const { toast } = useToast()
  const [target, setTarget] = useState<{ row: CountryOverlayRow; block: boolean } | null>(null)

  const columns = useMemo<AdminColumn<CountryOverlayRow>[]>(
    () => [
      {
        key: 'flag',
        header: '',
        type: 'text',
        width: 44,
        sortable: false,
        filterable: false,
        render: (r) => <span className="text-base">{r.flag || '🌐'}</span>,
      },
      { key: 'code', header: S.code, type: 'id', width: 80 },
      {
        key: 'name',
        header: S.country,
        type: 'text',
        minWidth: 180,
        render: (r) => (
          <Link to={`/geo/${r.code}`} className="text-accent hover:underline">
            {r.name}
          </Link>
        ),
      },
      { key: 'users', header: S.registrations, type: 'number', width: 110 },
      {
        key: 'is_blocked',
        header: S.status,
        type: 'enum',
        width: 140,
        accessor: (r) => (r.is_blocked ? 'blocked' : 'open'),
        options: [
          { value: 'open', label: S.open, status: 'active' },
          { value: 'blocked', label: S.blocked, status: 'banned' },
        ],
      },
      {
        key: 'overlay',
        header: overlayHeader,
        type: 'text',
        minWidth: 160,
        sortable: false,
        accessor: (r) => ovText(r, overlayField),
        render:
          renderOverlay ??
          ((r) => {
            const v = ovText(r, overlayField)
            return v ? <span className="text-primary">{v}</span> : <span className="text-tertiary">{S.none}</span>
          }),
      },
    ],
    [overlayField, overlayHeader, renderOverlay],
  )

  const fetcher = useCallback(fetchCountriesOverlay(overlayPath, overlayKey), [overlayPath, overlayKey])

  const rowActions = useMemo<RowAction<CountryOverlayRow>[]>(
    () => [
      {
        id: 'block',
        label: S.block,
        icon: <Ban size={13} />,
        danger: true,
        permission: PERMISSIONS.COUNTRIES_BLOCK,
        visible: (r) => !r.is_blocked,
        onSelect: (r) => setTarget({ row: r, block: true }),
      },
      {
        id: 'unblock',
        label: S.unblock,
        icon: <ShieldCheck size={13} />,
        permission: PERMISSIONS.COUNTRIES_BLOCK,
        visible: (r) => r.is_blocked,
        onSelect: (r) => setTarget({ row: r, block: false }),
      },
    ],
    [],
  )

  return (
    <>
      <PageHeader
        title={title}
        screenId={screenId}
        description={note}
        actions={
          <Link
            to="/geo"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.backToList}
          </Link>
        }
      />
      <DataTable<CountryOverlayRow>
        screenId={screenId}
        permission={PERMISSIONS.COUNTRIES_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.code}
        rowActions={rowActions}
        exportable
        emptyState={{ icon }}
      />

      {target && (
        <DangerDialog
          open
          onClose={() => setTarget(null)}
          screenId={screenId}
          level={4}
          title={`${target.block ? S.block : S.unblock}: ${target.row.name}`}
          description={target.block ? S.blockDesc : S.unblockDesc}
          confirmLabel={target.block ? S.block : S.unblock}
          objectName={target.row.code}
          impact={{
            items: [{ label: S.registrations, value: target.row.users, emphasis: target.block }],
            reversible: S.reverseWindow,
          }}
          onCreateRequest={async ({ reason }) => {
            try {
              await createFourEyesRequest({
                action: target.block ? 'block_country' : 'unblock_country',
                target: `country:${target.row.code}`,
                reason,
                impact: { users: target.row.users, country: target.row.code, scope: screenId },
              })
              toast(S.requestCreated, { tone: 'success' })
            } catch (e) {
              toast(S.requestFailed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}
    </>
  )
}
